import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BookingRepository } from '@app/store';
import { App } from '@capacitor/app';
import { NavController } from '@ionic/angular';
import { NavigationExtras } from '@angular/router';

interface AnimationOptions {
  animated?: boolean;
  animation?: any;
  animationDirection?: 'forward' | 'back';
}

type NavigationOptions = NavigationExtras & AnimationOptions;
import { CapacitorAppService } from '../capacitor';

@Injectable({
  providedIn: 'root',
})
export class RouterService {
  private initialized = false;
  private previousUrl: string = '';
  private currentUrl: string = '';

  constructor(
    private readonly capacitorAppService: CapacitorAppService,
    private readonly navController: NavController,
    private readonly router: Router,
    private readonly bookingRepository: BookingRepository,
  ) {
    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.url;
      }
    });
  }

  public initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    this.capacitorAppService.backButton$.subscribe((event) => {
      if (event.canGoBack) {
        window.history.back();
      } else {
        void App.exitApp();
      }
    });
  }

  navigateTo(url: string, options?: NavigationOptions): Promise<boolean> {
    const target = url.trim();
    // A single router command encodes "?" / "#" into the path segment. Full
    // in-app URLs with query or fragment must be parsed as a UrlTree instead.
    if (target.includes('?') || target.includes('#')) {
      return this.router.navigateByUrl(target, {
        replaceUrl: options?.replaceUrl,
        state: options?.state,
        info: options?.info,
      });
    }
    return this.navigateForward([target], options);
  }

  public navigateToLoginPage(options?: NavigationOptions): Promise<boolean> {
    return this.navigateForward(['/login'], options);
  }

  public navigateToRootPage(options?: NavigationOptions): Promise<boolean> {
    return this.navigateForward(['/'], options);
  }

  public navigateToSelectLocationPage(
    serviceId: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(
      ['services', serviceId, 'book', 'select-location'],
      options,
    );
  }

  public navigateToAccessInformationPage(
    bookingId: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(
      ['/property', bookingId, 'access-information'],
      options,
    );
  }

  public navigateToIdentityOTPPage(
    channel: string,
    options?: NavigationOptions,
    returnUrl?: string,
    phoneId?: string,
    emailId?: string,
  ): Promise<boolean> {
    return this.navigateForward(['/identity/otp'], {
      ...options,
      queryParams: {
        channel,
        ...(returnUrl ? { returnUrl } : {}),
        ...(phoneId ? { phoneId } : {}),
        ...(emailId ? { emailId } : {}),
      },
    });
  }

  public navigateToIdentityValidationPage(
    options?: NavigationOptions,
    returnUrl?: string,
  ): Promise<boolean> {
    return this.navigateForward(['/identity/validation'], {
      ...options,
      ...(returnUrl
        ? { queryParams: { ...options?.queryParams, returnUrl } }
        : {}),
    });
  }

  public navigateToRegisterPage(options?: NavigationOptions): Promise<boolean> {
    return this.navigateForward(['/register'], options);
  }

  public navigateToReservationsPage(
    reservationId?: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    if (reservationId) {
      return this.navigateForward(['/reservations', reservationId], options);
    } else {
      return this.navigateForward(['/reservations'], options);
    }
  }

  public navigateToReservationReviewPage(
    reservationId: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(
      ['/reservations', reservationId, 'review'],
      options,
    );
  }

  public navigateToMyPurchasesPage(
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(['/menu/my-purchases'], options);
  }

  public navigateToForgotPasswordPage(
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(['/forgot-password'], options);
  }

  public navigateToNoLocationsPage(
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(['/no-locations'], options);
  }

  public navigateToCheckInInstructionsPage(
    bookingId: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(['/property', bookingId, 'check-in'], options);
  }

  public navigateToCheckOutInstructionsPage(
    bookingId: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(['/property', bookingId, 'check-out'], options);
  }

  public navigateToWifiPage(
    bookingId: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(['/property', bookingId, 'wifi'], options);
  }

  public navigateToPlannerPage(options?: NavigationOptions): Promise<boolean> {
    return this.navigateForward(['/planner'], options);
  }

  public navigateToPlannerCustomizePage(
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(['/planner/customize'], options);
  }

  public navigateToPlannerItineraryPage(
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(['/planner/itinerary'], options);
  }

  public navigateToTutorialsPage(
    bookingId: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(['/property', bookingId, 'tutorials'], options);
  }

  /*   public async navigateToReservationsPage(
    reservationId: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(['reservations', reservationId], options);
  } */

  public async navigateToPropertyDetailsPage(
    bookingId: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(['/property', bookingId], options);
  }

  public async navigateToServiceDetailsPage(
    serviceId: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(['services', serviceId], options);
  }

  public async navigateToPaymentPage(
    serviceRequestId: string,
    clientSecret: string,
    customerSessionClientSecret?: string,
    options?: NavigationOptions & { paymentUrl?: string },
  ): Promise<boolean> {
    const { paymentUrl, ...navigationOptions } = options || {};
    if (paymentUrl) {
      const url = new URL(paymentUrl, window.location.origin);
      if (url.origin !== window.location.origin) {
        window.location.assign(url.toString());
        return true;
      }
      // The integration params must not ride along into an in-app route:
      // SyncGuard re-reads them on every navigation and keeps re-arming the
      // integration cleanup, which then hijacks the next navigation away from
      // this page. They belong to the deep-link entry, not to the payment step.
      for (const param of ['int', 'extid', 'extId']) {
        url.searchParams.delete(param);
      }
      return this.navigateForward(
        `${url.pathname}${url.search}${url.hash}`,
        navigationOptions,
      );
    }

    return this.navigateForward(
      [
        'payment',
        serviceRequestId,
        clientSecret,
        customerSessionClientSecret || '',
      ],
      navigationOptions,
    );
  }

  public navigateToCategoriesPage(
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(['/categories'], options);
  }

  public navigateToExplorePage(options?: NavigationOptions): Promise<boolean> {
    return this.navController.navigateRoot(['/explore'], options);
  }

  public navigateToNotificationsPage(
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(['/notifications'], options);
  }

  public navigateToHomePage(options?: NavigationOptions): Promise<boolean> {
    return this.navigateForward(['/home'], options);
  }

  public navigateToServicesByCategoryPage(
    categoryId: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(['/categories', categoryId], options);
  }

  public navigateToServicesByKindPage(
    serviceKind: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(['/service-kind', serviceKind], options);
  }

  public navigateToSchedulePage(
    serviceId: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(
      ['/services', serviceId, 'book', 'schedule'],
      options,
    );
  }

  public navigateToAdditionalInfoPage(
    serviceId: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(
      ['/services', serviceId, 'book', 'additional-info'],
      options,
    );
  }

  public navigateToCheckoutPage(
    serviceId: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(
      ['/services', serviceId, 'book', 'checkout'],
      options,
    );
  }

  public navigateToAddOnsPage(
    serviceId: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(
      ['/services', serviceId, 'book', 'add-ons'],
      options,
    );
  }

  public navigateToOptionsPage(
    serviceId: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(
      ['/services', serviceId, 'book', 'options'],
      options,
    );
  }

  public navigateToSchedulePageForEdit(
    serviceId: string,
    reservationId: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(
      ['/reservation-form', serviceId, reservationId],
      options,
    );
  }

  public navigateToReservationStatusPage(
    serviceRequestId: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(
      ['/reservations', serviceRequestId, 'status'],
      options,
    );
  }

  public navigateToReservationPaidPage(
    serviceRequestId: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(
      ['/reservations', serviceRequestId, 'paid'],
      options,
    );
  }

  public navigateToAccountPage(options?: NavigationOptions): Promise<boolean> {
    return this.navigateForward(['/account'], options);
  }

  public navigateToNotificationsSettingsPage(
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(['/notifications-settings'], options);
  }

  public navigateToProfilePage(options?: NavigationOptions): Promise<boolean> {
    return this.navigateForward(['/profile'], options);
  }

  public navigateToFamilyPage(options?: NavigationOptions): Promise<boolean> {
    return this.navigateForward(['/family'], options);
  }

  public navigateToErrorPage(options?: NavigationOptions): Promise<boolean> {
    return this.navigateForward(['/error'], options);
  }

  public navigateToInterestsPage(
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(['/interests'], options);
  }

  public navigateToBillingAddressPage(
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(['/billing-address'], options);
  }

  public navigateToWalletPage(options?: NavigationOptions): Promise<boolean> {
    return this.navigateForward(['/wallet'], options);
  }

  public navigateToQrScannerPage(
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(['/qr-scanner'], options);
  }

  /**
   * Navigates to the All Services page.
   * If both options and queryParams are provided, both will be merged into the navigation extras.
   * If only options is provided, it will be used.
   * If only queryParams is provided, it will be used.
   * If neither is provided, it will just navigate to /services.
   */
  public navigateToAllServicesPage(
    options?: NavigationOptions,
    queryParams?: { [key: string]: any },
  ): Promise<boolean> {
    const commands = ['/services'];
    // If either options or queryParams is provided, merge them into navigation extras
    if (
      (options && Object.keys(options).length > 0) ||
      (queryParams && Object.keys(queryParams).length > 0)
    ) {
      return this.navController.navigateForward(commands, {
        ...options,
        ...(queryParams ? { queryParams } : {}),
      });
    }
    // If neither options nor queryParams, just navigate
    return this.navigateForward(commands);
  }

  public navigateToBuildingPage(
    buildingId: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(['/building', buildingId], options);
  }

  public navigateToNewBookingPage(
    propertyId?: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    if (propertyId) {
      return this.navigateForward(['/booking/new', propertyId], options);
    } else {
      return this.navigateForward(['/qr-scanner'], options);
    }
  }

  /**
   * Navigates to the QR-entry identity verification flow for the given property
   * short code.  Used when a candidate booking is found for the property.
   */
  public navigateToPropertyEntryPage(
    shortCode: string,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navigateForward(['/property-entry', shortCode], options);
  }

  public navigateToMenuPage(options?: NavigationOptions): Promise<boolean> {
    return this.navigateForward(['/menu'], options);
  }

  public navigateToAboutPage(options?: NavigationOptions): Promise<boolean> {
    return this.navigateForward(['/about'], options);
  }

  public navigateBack(options?: NavigationOptions): Promise<boolean> {
    return this.navController.navigateBack(this.previousUrl || '/', {
      ...options,
      animationDirection: 'back',
    });
  }

  private navigateForward(
    commands: any[] | string | any,
    options?: NavigationOptions,
  ): Promise<boolean> {
    return this.navController.navigateForward(commands, options);
  }

  getPreviousUrl(): string {
    return this.previousUrl;
  }
}
